import { times, uniq } from 'lodash-es';
import { normalize } from 'normalizr';

import {
  type AssetsRelationSchema,
  type AssetsRelationSchemaWithId,
  httpDeleteAssetsByAssetIdRelationsByRelationTypeByRelatedToAssetId,
  httpGetAssetsByAssetIdRelations,
  httpGetAssetsRelationTypes,
  httpPostAssetsByAssetIdRelations,
  httpPostAssetsByAssetIdRelationsByRelationTypeByRelatedToAssetIdReverse,
} from '@iconik-io/iconik-api/src/services/assets';
import { httpPostSearch } from '@iconik-io/iconik-api/src/services/search';
import {
  asSearchItems,
  type SearchItemEntity,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import {
  clearNormalizedEntities,
  replaceEntities,
  updateEntities,
} from '@iconik-io/shared-lib/src/redux/entities/slice';
import { prepareQueryForMainSearch } from '@iconik-io/shared-lib/src/utils/search/prepare_query_for_main_search';

import type { AppDispatch, GetState } from '../../store/types';
import { filtersObjectToElastic } from '../pages/search/utils';
import {
  assetRelationCreated,
  assetRelationDeleted,
  clearAssetRelations,
  setAssetRelations,
  setAssetRelationsTypes,
} from './slice';
import type { RelationTarget } from './types';

export function assetRelationsTypesGet() {
  return (dispatch: AppDispatch) =>
    dispatch(httpGetAssetsRelationTypes()).then((response) =>
      dispatch(setAssetRelationsTypes(response.body.objects)),
    );
}

export function updateRelationsEntities(
  objects: SearchItemEntity[],
  id: string,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const { entities } = normalize(objects, [entitySchemas.asset_relations]);
    dispatch(updateEntities(entities));
    const ids = objects.map((object) => object.id);
    const currentIds = getState().assetRelations.relations[id] || [];
    const update = uniq([...currentIds, ...ids]);

    return dispatch(setAssetRelations({ assetId: id, relationIds: update }));
  };
}

export async function assetRelationsGetPages(
  dispatch: AppDispatch,
  id: string,
  pageNumber: number,
  pages: number,
  perPage: number,
): Promise<SearchItemEntity[]> {
  let page = pageNumber;
  const pagesRequests = times(pages, () => {
    page += 1;
    return dispatch(
      httpGetAssetsByAssetIdRelations(id, { page, per_page: perPage }),
    );
  });
  const pagesResponses = await Promise.all(pagesRequests);
  const pageObjects: SearchItemEntity[] = [];
  pagesResponses.forEach((relations) => {
    const objects = asSearchItems(relations.body.objects);
    pageObjects.push(...objects);
  });
  return pageObjects;
}

export function assetRelationsGet(id: string, data = {}) {
  const perPage = 100;
  return async (dispatch: AppDispatch): Promise<void> => {
    const response = await dispatch(
      httpGetAssetsByAssetIdRelations(id, { ...data, per_page: perPage }),
    );

    // clear relations
    dispatch(clearAssetRelations(id));
    dispatch(clearNormalizedEntities('asset_relations'));

    const { pages } = response.body;
    const objects = asSearchItems(response.body.objects);

    // pagination
    if (pages > 1) {
      const pagesObjects = await assetRelationsGetPages(
        dispatch,
        id,
        1,
        pages - 1,
        perPage,
      );
      dispatch(updateRelationsEntities([...objects, ...pagesObjects], id));
      return;
    }

    // one page only
    dispatch(updateRelationsEntities(objects, id));
  };
}

export function assetRelationsCreate(
  id: string,
  data: AssetsRelationSchema,
  item: SearchItemEntity,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(httpPostAssetsByAssetIdRelations(id, data)).then((response) => {
      // add the relation to the item
      const itemToAdd: SearchItemEntity = { ...item, relations: [] };
      const newRelation: Partial<AssetsRelationSchemaWithId> = {
        description: response.body.description,
        relation_type: response.body.relation_type,
        related_from_asset_id: id === item.id ? undefined : id,
        related_to_asset_id:
          id === item.id ? response.body.related_to_asset_id : undefined,
      };
      const relations = getState().entities.asset_relations;
      const itemRelations = relations[item.id]?.relations ?? [];
      itemToAdd.relations = itemRelations.concat(newRelation);

      const source =
        newRelation.related_from_asset_id || newRelation.related_to_asset_id;
      dispatch(
        assetRelationCreated({
          source: response.body.asset_id,
          target: response.body.related_to_asset_id,
          type: response.body.relation_type,
        }),
      );
      if (!source) {
        return;
      }
      dispatch(updateRelationsEntities([itemToAdd], source));
    });
}

export function assetRelationsDelete(
  source: string,
  target: string,
  relation: string,
) {
  return (dispatch: AppDispatch): Promise<unknown> =>
    // delete the relation in both directions
    dispatch(
      httpDeleteAssetsByAssetIdRelationsByRelationTypeByRelatedToAssetId(
        source,
        relation,
        target,
      ),
    ).then(() => {
      dispatch(
        assetRelationDeleted({ target: source, source: target, relation }),
      );
      return dispatch(assetRelationDeleted({ source, target, relation }));
    });
}

export const searchAssetToRelate = (id: string, query = '', perPage = 100) =>
  httpPostSearch(
    {
      query: query ? prepareQueryForMainSearch(query) : query,
      include_fields: [
        'id',
        'title',
        'object_type',
        'type',
        'files',
        'formats',
        'keyframes',
      ],
      doc_types: ['assets'],
      filter: {
        operator: 'NOT',
        terms: filtersObjectToElastic({
          id,
          status: 'DELETED',
        }),
      },
      sort: [{ name: 'date_created', order: 'desc' }],
    },
    { save_search_history: false, per_page: perPage },
  );

export function assetRelationsReverse(
  searchItem: SearchItemEntity,
  relationTarget: RelationTarget,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> =>
    dispatch(
      httpPostAssetsByAssetIdRelationsByRelationTypeByRelatedToAssetIdReverse(
        relationTarget.sourceId,
        relationTarget.relationType,
        relationTarget.targetId,
      ),
    ).then((response) => {
      const updatedItem: SearchItemEntity = { ...searchItem, relations: [] };

      const assetRelations = getState().entities.asset_relations;
      const relations = assetRelations[searchItem.id]?.relations ?? [];

      const newRelation: Partial<AssetsRelationSchemaWithId> = {
        description: response.body.description,
        relation_type: response.body.relation_type,
        related_from_asset_id:
          relationTarget.assetId !== response.body.related_to_asset_id
            ? relationTarget.assetId
            : undefined,
        related_to_asset_id:
          relationTarget.assetId === response.body.related_to_asset_id
            ? response.body.related_to_asset_id
            : undefined,
      };

      const newRelations = relations.map((item) => {
        const { related_from_asset_id, related_to_asset_id } = item;
        const { originalRelation } = relationTarget;
        const match =
          (related_to_asset_id === originalRelation.related_to_asset_id &&
            originalRelation.related_to_asset_id !== undefined) ||
          (related_from_asset_id === originalRelation.related_from_asset_id &&
            originalRelation.related_from_asset_id !== undefined);

        return match ? newRelation : item;
      });

      updatedItem.relations = newRelations;

      const { entities } = normalize({ [searchItem.id]: updatedItem }, [
        entitySchemas.asset_relations,
      ]);

      return dispatch(replaceEntities(entities));
    });
}
